package com.evoapps.evoshift.ui.finance

import androidx.compose.runtime.Composable
import com.evoapps.evoshift.domain.market.payroll.MonthPayFactors
import com.evoapps.evoshift.domain.model.CompensationModel
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.payroll_below_minimum_hourly
import evoshift.composeapp.generated.resources.payroll_holiday_pay
import evoshift.composeapp.generated.resources.payroll_holiday_variable
import evoshift.composeapp.generated.resources.payroll_leave_year
import evoshift.composeapp.generated.resources.payroll_minimum_top_up
import evoshift.composeapp.generated.resources.payroll_overtime_100
import evoshift.composeapp.generated.resources.payroll_overtime_50
import evoshift.composeapp.generated.resources.payroll_overtime_pay
import evoshift.composeapp.generated.resources.payroll_rate_change
import evoshift.composeapp.generated.resources.payroll_rate_from
import evoshift.composeapp.generated.resources.payroll_rate_hourly
import evoshift.composeapp.generated.resources.payroll_rate_monthly
import evoshift.composeapp.generated.resources.payroll_salary_reduction
import evoshift.composeapp.generated.resources.payroll_settlement_hours
import evoshift.composeapp.generated.resources.payroll_sick_base
import evoshift.composeapp.generated.resources.payroll_sick_benefit
import evoshift.composeapp.generated.resources.payroll_sick_pay
import evoshift.composeapp.generated.resources.payroll_sunday_holiday
import evoshift.composeapp.generated.resources.payroll_time_off_owed
import evoshift.composeapp.generated.resources.payroll_vacation_in_salary
import evoshift.composeapp.generated.resources.payroll_paid_leave
import evoshift.composeapp.generated.resources.payroll_unpaid_reduction
import evoshift.composeapp.generated.resources.payroll_extra_work
import evoshift.composeapp.generated.resources.payroll_benefit_note
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.getString
import org.jetbrains.compose.resources.stringResource

/**
 * The texts of the month's pay lines and notes, with "{1}", "{2}" where the figures go, so the
 * same lines serve the screens (stringResource) and the printed report (getString).
 */
data class PayFactorTexts(
    val overtimePay: String,
    val overtime50: String,
    val overtime100: String,
    val sundayHoliday: String,
    val minimumTopUp: String,
    // SH-57: leave and pay changes, each marked as an estimate.
    val sickPay: String,
    val sickBenefit: String,
    val salaryReduction: String,
    val holidayPay: String,
    val holidayVariable: String,
    val rateChange: String,
    // Notes.
    val timeOffOwed: String,
    val settlementHours: String,
    val belowMinimumHourly: String,
    val sickBase: String,
    val vacationInSalary: String,
    val leaveYear: String,
    val rateFrom: String,
    val rateHourly: String,
    val rateMonthly: String,
    // SH-223: other time off and work outside the shifts.
    val paidLeave: String = "",
    val unpaidLeave: String = "",
    val extraWork: String = "",
    val benefitNote: String = ""
) {
    companion object {
        private const val A = "{1}"
        private const val B = "{2}"

        @Composable
        fun load() = PayFactorTexts(
            stringResource(Res.string.payroll_overtime_pay), stringResource(Res.string.payroll_overtime_50, A),
            stringResource(Res.string.payroll_overtime_100, A), stringResource(Res.string.payroll_sunday_holiday, A),
            stringResource(Res.string.payroll_minimum_top_up),
            stringResource(Res.string.payroll_sick_pay, A), stringResource(Res.string.payroll_sick_benefit, A),
            stringResource(Res.string.payroll_salary_reduction, A), stringResource(Res.string.payroll_holiday_pay, A),
            stringResource(Res.string.payroll_holiday_variable), stringResource(Res.string.payroll_rate_change),
            stringResource(Res.string.payroll_time_off_owed, A), stringResource(Res.string.payroll_settlement_hours, A, B),
            stringResource(Res.string.payroll_below_minimum_hourly, A), stringResource(Res.string.payroll_sick_base, A),
            stringResource(Res.string.payroll_vacation_in_salary, A), stringResource(Res.string.payroll_leave_year, A, B),
            stringResource(Res.string.payroll_rate_from, A, B), stringResource(Res.string.payroll_rate_hourly, A),
            stringResource(Res.string.payroll_rate_monthly, A),
            stringResource(Res.string.payroll_paid_leave, A), stringResource(Res.string.payroll_unpaid_reduction, A),
            stringResource(Res.string.payroll_extra_work, A), stringResource(Res.string.payroll_benefit_note, A)
        )

        suspend fun get() = PayFactorTexts(
            getString(Res.string.payroll_overtime_pay), getString(Res.string.payroll_overtime_50, A),
            getString(Res.string.payroll_overtime_100, A), getString(Res.string.payroll_sunday_holiday, A),
            getString(Res.string.payroll_minimum_top_up),
            getString(Res.string.payroll_sick_pay, A), getString(Res.string.payroll_sick_benefit, A),
            getString(Res.string.payroll_salary_reduction, A), getString(Res.string.payroll_holiday_pay, A),
            getString(Res.string.payroll_holiday_variable), getString(Res.string.payroll_rate_change),
            getString(Res.string.payroll_time_off_owed, A), getString(Res.string.payroll_settlement_hours, A, B),
            getString(Res.string.payroll_below_minimum_hourly, A), getString(Res.string.payroll_sick_base, A),
            getString(Res.string.payroll_vacation_in_salary, A), getString(Res.string.payroll_leave_year, A, B),
            getString(Res.string.payroll_rate_from, A, B), getString(Res.string.payroll_rate_hourly, A),
            getString(Res.string.payroll_rate_monthly, A),
            getString(Res.string.payroll_paid_leave, A), getString(Res.string.payroll_unpaid_reduction, A),
            getString(Res.string.payroll_extra_work, A), getString(Res.string.payroll_benefit_note, A)
        )
    }
}

private fun String.fill(first: String, second: String = "") = replace("{1}", first).replace("{2}", second)

/** A signed amount: "−1 400,00 zł" for what the month takes off, the plain amount otherwise. */
fun signedMoney(amount: Double, currency: String): String =
    if (amount < 0) "−" + formatMoney(-amount, currency) else formatMoney(amount, currency)

/**
 * SH-57: what the month adds to (or takes off) the shifts' pay, as separate lines of the estimate
 * (label to signed amount), all already in the gross: the pay for the hours over the norm, the
 * overtime supplements, Sunday and holiday work without a day off, the minimum-wage top-up, sick
 * pay and the ZUS benefit, the salary not paid for days of illness, holiday pay and a pay change
 * during the month. Lines with nothing to pay are left out.
 */
fun payFactorLines(factors: MonthPayFactors, currency: String, texts: PayFactorTexts): List<Pair<String, String>> = buildList {
    fun hours(value: Double) = formatDecimal(value, 1)
    fun money(value: Double) = signedMoney(value, currency)
    if (factors.overtimePay > 0) add(texts.overtimePay to money(factors.overtimePay))
    if (factors.overtime50Supplement > 0) add(texts.overtime50.fill(hours(factors.overtime50Hours)) to money(factors.overtime50Supplement))
    if (factors.overtime100Supplement > 0) add(texts.overtime100.fill(hours(factors.overtime100Hours)) to money(factors.overtime100Supplement))
    if (factors.sundayHolidaySupplement > 0) add(texts.sundayHoliday.fill(hours(factors.sundayHolidayHours)) to money(factors.sundayHolidaySupplement))
    if (factors.minimumWageTopUp > 0) add(texts.minimumTopUp to money(factors.minimumWageTopUp))
    if (factors.sickPay > 0) add(texts.sickPay.fill(factors.sickPayDays.toString()) to money(factors.sickPay))
    if (factors.sickBenefit > 0) add(texts.sickBenefit.fill(factors.sickBenefitDays.toString()) to money(factors.sickBenefit))
    if (factors.salaryReduction > 0) add(texts.salaryReduction.fill(factors.sickDays.toString()) to money(-factors.salaryReduction))
    if (factors.holidayPay > 0) add(texts.holidayPay.fill(factors.vacationDays.toString()) to money(factors.holidayPay))
    if (factors.holidayVariablePay > 0) add(texts.holidayVariable to money(factors.holidayVariablePay))
    // SH-223: blood donation, childcare, force majeure (half), urlop okolicznościowy, time off in lieu; unpaid leave; work outside the shifts.
    if (factors.paidLeavePay > 0) add(texts.paidLeave.fill(hours(factors.paidLeaveHours + factors.halfPaidLeaveHours)) to money(factors.paidLeavePay))
    if (factors.unpaidLeaveReduction > 0) {
        add(texts.unpaidLeave.fill((factors.unpaidLeaveDays + factors.benefitLeaveDays + factors.halfPaidLeaveDays).toString()) to money(-factors.unpaidLeaveReduction))
    }
    if (factors.extraWorkPay > 0) add(texts.extraWork.fill(hours(factors.extraWorkHours)) to money(factors.extraWorkPay))
    if (factors.rateChangeAdjustment != 0.0) add(texts.rateChange to money(factors.rateChangeAdjustment))
}

/**
 * SH-57: what explains the lines: time off owed, a settlement period still running, a rate under
 * the minimum, the sick-pay base, leave paid within a salary, the year's leave, and the pay terms
 * of each part of a month in which they changed.
 */
fun payFactorNotes(factors: MonthPayFactors, currency: String, texts: PayFactorTexts, rateWarning: Boolean = true): List<String> = buildList {
    if (factors.timeOffInLieuHours > 0) add(texts.timeOffOwed.fill(formatDecimal(factors.timeOffInLieuHours, 1)))
    if (!factors.settled && factors.periodNormHours > 0) {
        add(texts.settlementHours.fill(formatDecimal(factors.periodWorkedHours, 1), formatDecimal(factors.periodNormHours, 1)))
    }
    factors.minimumHourlyRate?.takeIf { rateWarning }?.let { add(texts.belowMinimumHourly.fill(formatMoney(it, currency))) }
    if (factors.sickPayBase > 0) add(texts.sickBase.fill(formatMoney(factors.sickPayBase, currency)))
    // SH-223: maternity, paternity and parental leave, the care allowance: ZUS pays, not estimated.
    if (factors.benefitLeaveDays > 0 && texts.benefitNote.isNotEmpty()) add(texts.benefitNote.fill(factors.benefitLeaveDays.toString()))
    if (factors.vacationDays > 0 && factors.holidayPay == 0.0) add(texts.vacationInSalary.fill(factors.vacationDays.toString()))
    if (factors.vacationDays > 0 && factors.leaveEntitlementDays > 0) {
        add(texts.leaveYear.fill(factors.leaveUsedDays.toString(), factors.leaveEntitlementDays.toString()))
    }
    if (factors.rateChanged) {
        factors.rateChanges.forEach { period ->
            val date = runCatching { LocalDate.parse(period.effectiveFrom) }.getOrNull()
                ?.let { "${it.day.toString().padStart(2, '0')}.${(it.month.ordinal + 1).toString().padStart(2, '0')}" } ?: period.effectiveFrom
            val terms = when (val pay = period.compensation) {
                is CompensationModel.Hourly -> texts.rateHourly.fill(formatMoney(pay.rate, currency))
                is CompensationModel.MonthlyFixed -> texts.rateMonthly.fill(formatMoney(pay.baseSalary, currency))
            }
            add(texts.rateFrom.fill(date, terms))
        }
    }
}

@Composable
fun payFactorLines(factors: MonthPayFactors, currency: String): List<Pair<String, String>> =
    payFactorLines(factors, currency, PayFactorTexts.load())

@Composable
fun payFactorNotes(factors: MonthPayFactors, currency: String, rateWarning: Boolean = true): List<String> =
    payFactorNotes(factors, currency, PayFactorTexts.load(), rateWarning)
